import { useQuery } from '@tanstack/react-query';
import * as leaderboardRepo from '@/lib/repositories/leaderboard.repo';
import type { BoardScope, LeaderboardType } from '@/lib/domain/leaderboard';
import { toLeaderboardEntry, toUserRank } from '@/lib/mappers/leaderboard.mapper';
import { queryKeys } from '@/lib/queries/queryKeys';
import { useAuthStore } from '@/stores/auth.store';

export function useLeaderboard(
  type: LeaderboardType,
  currentUserId?: string,
  season?: string,
  enabled = true,
) {
  // Boards are public reads; they only wait for the session check so a member's request carries the token.
  const sessionKnown = useAuthStore((state) => state.status !== 'loading');
  return useQuery({
    // Rows carry isCurrentUser, so the cache is scoped to the viewer (guest or member).
    queryKey: queryKeys.leaderboard.list(type, season, currentUserId),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getLeaderboard(type, 50, 0, season);
      if (error) throw new Error('Failed to fetch leaderboard');
      return data.map((entry) => toLeaderboardEntry(entry, currentUserId));
    },
    enabled: sessionKnown && enabled,
  });
}

export function useUserRank(
  userId: string,
  type: LeaderboardType = 'global',
  season?: string,
  enabled = true,
) {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  return useQuery({
    queryKey: queryKeys.leaderboard.user(userId, type, season),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getUserRank(type, season);
      if (error) throw new Error('Failed to fetch user rank');
      return data ? toUserRank(data) : null;
    },
    enabled: isAuthenticated && !!userId && enabled,
  });
}

/**
 * Auction board. `enabled` lets the screen hold the request back until the
 * Auction tab is actually selected, so visiting the ranked leaderboard doesn't
 * fire a second, unused leaderboard fetch.
 */
export function useAuctionLeaderboard(
  type: LeaderboardType,
  currentUserId?: string,
  enabled = true,
) {
  const sessionKnown = useAuthStore((state) => state.status !== 'loading');
  return useQuery({
    queryKey: queryKeys.leaderboard.auctionList(type, currentUserId),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getAuctionLeaderboard(type);
      if (error) throw new Error('Failed to fetch auction leaderboard');
      return data.map((entry) => toLeaderboardEntry(entry, currentUserId));
    },
    enabled: sessionKnown && enabled,
  });
}

export function useAuctionUserRank(
  userId: string,
  type: LeaderboardType = 'global',
  enabled = true,
) {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  return useQuery({
    queryKey: queryKeys.leaderboard.auctionUser(userId, type),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getAuctionUserRank(type);
      if (error) throw new Error('Failed to fetch auction user rank');
      return data ? toUserRank(data) : null;
    },
    enabled: isAuthenticated && !!userId && enabled,
  });
}

export function useTicTacToeLeaderboard(
  type: LeaderboardType,
  currentUserId?: string,
  enabled = true,
) {
  const sessionKnown = useAuthStore((state) => state.status !== 'loading');
  return useQuery({
    queryKey: queryKeys.leaderboard.ticTacToeList(type, currentUserId),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getTicTacToeLeaderboard(type);
      if (error) throw new Error('Failed to fetch Tic Tac Toe leaderboard');
      return data.map((entry) => toLeaderboardEntry(entry, currentUserId));
    },
    enabled: sessionKnown && enabled,
  });
}

export function useTicTacToeUserRank(
  userId: string,
  type: LeaderboardType = 'global',
  enabled = true,
) {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  return useQuery({
    queryKey: queryKeys.leaderboard.ticTacToeUser(userId, type),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getTicTacToeUserRank(type);
      if (error) throw new Error('Failed to fetch Tic Tac Toe user rank');
      return data ? toUserRank(data) : null;
    },
    enabled: isAuthenticated && !!userId && enabled,
  });
}

export function useLeaderboardSeasons() {
  const sessionKnown = useAuthStore((state) => state.status !== 'loading');
  return useQuery({
    queryKey: queryKeys.leaderboard.seasons(),
    queryFn: async () => {
      const { data, error } = await leaderboardRepo.getLeaderboardSeasons();
      if (error) throw new Error('Failed to fetch leaderboard seasons');
      return data;
    },
    enabled: sessionKnown,
    staleTime: 5 * 60 * 1000,
  });
}

function boardKey(scope: BoardScope): string {
  return scope.kind === 'global' ? 'global' : scope.kind === 'country' ? `country:${scope.country}` : `club:${scope.club}`;
}

/** Players on any board (world, a country, a club) for one mode. */
export function useBoard(
  mode: leaderboardRepo.BoardMode,
  scope: BoardScope,
  currentUserId?: string,
  season?: string,
  enabled = true,
) {
  const sessionKnown = useAuthStore((state) => state.status !== 'loading');
  return useQuery({
    // Rows carry isCurrentUser, so the cache is scoped to the viewer as well as the board.
    queryKey: queryKeys.leaderboard.board(mode, boardKey(scope), season, currentUserId),
    queryFn: async () => {
      const entries = await leaderboardRepo.getBoard(mode, scope, 50, 0, season);
      return entries.map((entry) => toLeaderboardEntry(entry, currentUserId));
    },
    enabled: sessionKnown && enabled,
  });
}

export function useBoardRank(
  mode: leaderboardRepo.BoardMode,
  scope: BoardScope,
  userId: string | undefined,
  season?: string,
  enabled = true,
) {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');
  return useQuery({
    queryKey: queryKeys.leaderboard.boardRank(mode, boardKey(scope), userId ?? '', season),
    queryFn: async () => {
      const data = await leaderboardRepo.getBoardRank(mode, scope, season);
      return data ? { entry: toUserRank(data), nextTarget: data.nextTarget ?? null } : null;
    },
    enabled: isAuthenticated && !!userId && enabled,
  });
}

export function useStandings(
  mode: leaderboardRepo.BoardMode,
  kind: 'country' | 'club',
  viewerId?: string,
  enabled = true,
) {
  const sessionKnown = useAuthStore((state) => state.status !== 'loading');
  return useQuery({
    queryKey: queryKeys.leaderboard.standings(mode, kind, viewerId),
    queryFn: () => leaderboardRepo.getStandings(mode, kind),
    enabled: sessionKnown && enabled,
    staleTime: 60 * 1000,
  });
}
